import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';

const contactMethods = [
  {
    icon: Mail,
    title: 'Email Us',
    description: 'For general inquiries and support',
    value: 'hello@agentick.in',
    href: 'mailto:hello@agentick.in',
  },
  {
    icon: Phone,
    title: 'Call Us',
    description: 'Mon-Fri, 10 AM – 6 PM IST',
    value: '+91 80-XXXX-XXXX',
    href: 'tel:+9180XXXXXXXX',
  },
  {
    icon: MapPin,
    title: 'Visit Us',
    description: 'Our headquarters',
    value: 'Bengaluru, Karnataka, India',
    href: '#',
  },
  {
    icon: Clock,
    title: 'Response Time',
    description: 'We typically reply within',
    value: 'Under 24 hours',
    href: '#',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);
    // Simulate send then open mailto
    setTimeout(() => {
      const mailtoLink = `mailto:hello@agentick.in?subject=${encodeURIComponent(form.subject || 'Contact from website')}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
      window.open(mailtoLink, '_blank');
      toast.success('Opening your email client...');
      setSending(false);
      setForm({ name: '', email: '', subject: '', message: '' });
    }, 600);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-primary/[0.05] blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(hsl(210 20% 95% / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(210 20% 95% / 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Badge variant="premium" className="mb-5">
              <MessageSquare size={12} className="mr-1" />
              Get in Touch
            </Badge>
          </motion.div>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-5 leading-tight"
          >
            We’d Love to{' '}
            <span className="gradient-text-primary">Hear From You</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed max-w-xl mx-auto"
          >
            Have a question about Agentick? Want to schedule a demo? Or just want to say hello? Reach out and we’ll get back to you quickly.
          </motion.p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {contactMethods.map((method, i) => (
            <motion.a
              key={method.title}
              href={method.href}
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
              className="glass-card rounded-2xl p-6 text-center hover:border-primary/15 transition-colors duration-300 block"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <method.icon size={20} className="text-primary" />
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground mb-1">{method.title}</h3>
              <p className="text-xs text-muted-foreground font-body mb-2">{method.description}</p>
              <p className="text-sm font-medium text-foreground font-body">{method.value}</p>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-3"
          >
            <div className="glass-card rounded-2xl p-8 lg:p-10">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-2">Send us a message</h2>
              <p className="text-sm text-muted-foreground font-body mb-8">Fill out the form and we’ll be in touch shortly.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-foreground font-body">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      className="bg-secondary/50 border-border/60 h-11 font-body"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-foreground font-body">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={handleChange}
                      className="bg-secondary/50 border-border/60 h-11 font-body"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm font-medium text-foreground font-body">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="What is this about?"
                    value={form.subject}
                    onChange={handleChange}
                    className="bg-secondary/50 border-border/60 h-11 font-body"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium text-foreground font-body">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us more about what you need..."
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="bg-secondary/50 border-border/60 font-body resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  variant="premium"
                  size="lg"
                  className="w-full sm:w-auto"
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Quick links */}
            <div className="glass-card rounded-2xl p-7">
              <h3 className="font-heading text-base font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-3">
                {[
                  { label: 'Start your free trial', href: 'https://app.agentick.in' },
                  { label: 'View pricing plans', href: '/#pricing' },
                  { label: 'Read the docs', href: '#' },
                  { label: 'Join our community', href: '#' },
                ].map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.href.startsWith('http') ? '_blank' : undefined}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 font-body"
                    >
                      <ArrowRight size={14} className="text-primary" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* FAQ teaser */}
            <div className="glass-card rounded-2xl p-7">
              <h3 className="font-heading text-base font-semibold text-foreground mb-2">Looking for answers?</h3>
              <p className="text-sm text-muted-foreground font-body leading-relaxed mb-4">
                Check our FAQ section on the homepage for quick answers to common questions about Agentick.
              </p>
              <Button variant="glass" size="sm" asChild>
                <a href="/#benefits">Browse FAQ</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
